import React from "react";

/**
 * AuraVeil proprietary tarot face assets — Minimalist Sacred Geometry.
 *
 * Style rules:
 *   ▸ Soft Gold (#D4AF37) line-art on deep matte black.
 *   ▸ stroke-only (fill:none) glyphs — every symbol is a geometric line study.
 *   ▸ All glyphs centred at (0,0), designed to sit comfortably in ±22 units.
 *   ▸ Suit glyphs are the atomic vocabulary; pip cards compose them along
 *     sacred-geometry grids; court cards pair one suit glyph with a small
 *     Cormorant rank label.
 *
 * Everything below is pure SVG fragments — the parent CardFace supplies the
 * viewBox, frame border, and Cormorant Garamond title band.
 */

// ─── Suit glyphs (atomic) ───────────────────────────────────────────────
const CupGlyph = () => (
  <g>
    <path d="M -5 -6 L 5 -6 Q 5 4 0 5 Q -5 4 -5 -6 Z" />
    <path d="M 0 5 L 0 8" />
    <path d="M -3 8 L 3 8" />
  </g>
);

const SwordGlyph = () => (
  <g>
    <path d="M 0 -8 L 0 8" />
    <path d="M -4 -4 L 4 -4" />
    <circle cx="0" cy="-8" r="1.2" />
  </g>
);

const WandGlyph = () => (
  <g>
    <path d="M 0 -8 L 0 8" />
    <path d="M -2 -8 Q 0 -10 2 -8" />
    <path d="M -2 8 Q 0 10 2 8" />
    <circle cx="0" cy="0" r="0.9" />
  </g>
);

const PentacleGlyph = () => (
  <g>
    <circle cx="0" cy="0" r="7.5" />
    <path d="M 0 -6 L 1.9 -1.9 L 6 -1.9 L 2.7 0.7 L 3.7 5 L 0 2.4 L -3.7 5 L -2.7 0.7 L -6 -1.9 L -1.9 -1.9 Z" />
  </g>
);

const SUIT = {
  Cups: CupGlyph,
  Swords: SwordGlyph,
  Wands: WandGlyph,
  Pentacles: PentacleGlyph,
};

// ─── Pip layouts (sacred-geometry grids for Ace..Ten) ───────────────────
const PIP_LAYOUTS = {
  1: [{ x: 50, y: 50, scale: 2.4 }],
  2: [
    { x: 50, y: 32 },
    { x: 50, y: 68 },
  ],
  3: [
    { x: 50, y: 25 },
    { x: 30, y: 62 },
    { x: 70, y: 62 },
  ],
  4: [
    { x: 30, y: 30 },
    { x: 70, y: 30 },
    { x: 30, y: 70 },
    { x: 70, y: 70 },
  ],
  5: [
    { x: 28, y: 26 },
    { x: 72, y: 26 },
    { x: 50, y: 50 },
    { x: 28, y: 74 },
    { x: 72, y: 74 },
  ],
  6: [
    { x: 28, y: 22 },
    { x: 72, y: 22 },
    { x: 28, y: 50 },
    { x: 72, y: 50 },
    { x: 28, y: 78 },
    { x: 72, y: 78 },
  ],
  7: [
    { x: 50, y: 14 },
    { x: 28, y: 32 },
    { x: 72, y: 32 },
    { x: 50, y: 50 },
    { x: 28, y: 68 },
    { x: 72, y: 68 },
    { x: 50, y: 86 },
  ],
  8: [
    { x: 28, y: 18 },
    { x: 72, y: 18 },
    { x: 28, y: 40 },
    { x: 72, y: 40 },
    { x: 28, y: 62 },
    { x: 72, y: 62 },
    { x: 28, y: 84 },
    { x: 72, y: 84 },
  ],
  9: [
    { x: 26, y: 20 },
    { x: 50, y: 20 },
    { x: 74, y: 20 },
    { x: 26, y: 50 },
    { x: 50, y: 50 },
    { x: 74, y: 50 },
    { x: 26, y: 80 },
    { x: 50, y: 80 },
    { x: 74, y: 80 },
  ],
  10: [
    { x: 26, y: 18 },
    { x: 50, y: 18 },
    { x: 74, y: 18 },
    { x: 26, y: 40 },
    { x: 74, y: 40 },
    { x: 26, y: 62 },
    { x: 74, y: 62 },
    { x: 26, y: 84 },
    { x: 50, y: 84 },
    { x: 74, y: 84 },
  ],
};

// ─── Major Arcana glyphs (each drawn centred at 0,0, ±22 unit box) ──────
const MajorGlyphs = {
  "The Fool": () => (
    <g>
      <circle cx="0" cy="0" r="18" />
      <path d="M -6 -8 Q 0 -14 6 -8" />
      <circle cx="-10" cy="10" r="2.6" />
    </g>
  ),
  "The Magician": () => (
    <g>
      <path d="M -10 -6 Q -10 -14 -4 -10 Q 0 -8 4 -10 Q 10 -14 10 -6 Q 10 -2 4 -4 Q 0 -6 -4 -4 Q -10 -2 -10 -6 Z" />
      <path d="M -14 6 L 14 6" />
      <circle cx="-8" cy="12" r="1.5" />
      <circle cx="-2.5" cy="12" r="1.5" />
      <circle cx="3" cy="12" r="1.5" />
      <circle cx="9" cy="12" r="1.5" />
    </g>
  ),
  "The High Priestess": () => (
    <g>
      <path d="M -12 -14 L -12 14" />
      <path d="M 12 -14 L 12 14" />
      <path d="M -6 -4 A 8 8 0 1 0 6 -4 A 6 6 0 1 1 -6 -4 Z" />
      <path d="M -12 14 L 12 14" />
    </g>
  ),
  "The Empress": () => (
    <g>
      <circle cx="0" cy="-5" r="7" />
      <path d="M 0 2 L 0 12" />
      <path d="M -5 8 L 5 8" />
      <path d="M -8 -14 L -4 -10 L 0 -14 L 4 -10 L 8 -14" />
    </g>
  ),
  "The Emperor": () => (
    <g>
      <rect x="-10" y="-4" width="20" height="18" />
      <path d="M -12 -4 L -8 -12 L -4 -4 L 0 -12 L 4 -4 L 8 -12 L 12 -4" />
    </g>
  ),
  "The Hierophant": () => (
    <g>
      <path d="M -12 -10 L 12 -10" />
      <path d="M -10 -4 L 10 -4" />
      <path d="M -8 2 L 8 2" />
      <path d="M -4 2 L -4 14 M -4 14 L -8 18 L 0 18" />
      <path d="M 4 2 L 4 14 M 4 14 L 8 18 L 0 18" />
    </g>
  ),
  "The Lovers": () => (
    <g>
      <circle cx="-6" cy="0" r="9" />
      <circle cx="6" cy="0" r="9" />
    </g>
  ),
  "The Chariot": () => (
    <g>
      <path d="M -12 -4 L 12 -4 L 8 6 L -8 6 Z" />
      <circle cx="-8" cy="12" r="4" />
      <circle cx="8" cy="12" r="4" />
      <path d="M 0 -4 L 0 -12" />
      <path d="M -3 -12 L 3 -12" />
    </g>
  ),
  Strength: () => (
    <g>
      <path d="M -14 0 Q -14 -8 -6 -8 Q 0 -8 0 0 Q 0 8 6 8 Q 14 8 14 0 Q 14 -8 6 -8 Q 0 -8 0 0 Q 0 8 -6 8 Q -14 8 -14 0 Z" />
    </g>
  ),
  "The Hermit": () => (
    <g>
      <rect x="-5" y="-6" width="10" height="14" />
      <path d="M -3 -6 L -3 8 M 3 -6 L 3 8" />
      <path d="M 0 -6 L 0 -12" />
      <path d="M -8 -12 L 8 -12" />
      <path d="M -10 12 Q 0 16 10 12" />
    </g>
  ),
  "Wheel of Fortune": () => (
    <g>
      <circle cx="0" cy="0" r="14" />
      <circle cx="0" cy="0" r="4" />
      <path d="M -14 0 L 14 0" />
      <path d="M 0 -14 L 0 14" />
      <path d="M -10 -10 L 10 10" />
      <path d="M -10 10 L 10 -10" />
    </g>
  ),
  Justice: () => (
    <g>
      <path d="M 0 -12 L 0 12" />
      <path d="M -14 -6 L 14 -6" />
      <circle cx="-10" cy="0" r="5" />
      <circle cx="10" cy="0" r="5" />
      <path d="M -6 12 L 6 12" />
    </g>
  ),
  "The Hanged Man": () => (
    <g>
      <path d="M -12 -12 L 12 -12" />
      <path d="M 0 -12 L 0 -2" />
      <circle cx="0" cy="2" r="4" />
      <path d="M 0 6 L 0 12" />
      <path d="M -4 16 L 4 16 L 0 12 Z" />
    </g>
  ),
  Death: () => (
    <g>
      <path d="M -12 8 Q -12 -6 0 -6 Q 12 -6 12 8" />
      <path d="M -8 12 L 8 12" />
      <path d="M -4 -6 L -4 -10" />
      <path d="M 4 -6 L 4 -10" />
    </g>
  ),
  Temperance: () => (
    <g>
      <path d="M -10 -8 L -4 -8 Q -4 -2 -7 -2 Q -10 -2 -10 -8 Z" />
      <path d="M 4 -8 L 10 -8 Q 10 -2 7 -2 Q 4 -2 4 -8 Z" />
      <path d="M -7 -2 Q -3 4 0 2 Q 3 0 7 -2" />
      <path d="M -7 -2 L -7 4 M 7 -2 L 7 4" />
      <path d="M -10 8 L -4 8 M 4 8 L 10 8" />
    </g>
  ),
  "The Devil": () => (
    <g>
      <circle cx="0" cy="0" r="12" />
      <path d="M 0 12 L 3.5 1 L 12 1 L 5 -5 L 7 -12 L 0 -8 L -7 -12 L -5 -5 L -12 1 L -3.5 1 Z" />
    </g>
  ),
  "The Tower": () => (
    <g>
      <path d="M -8 12 L -8 -6 L -12 -10 L -12 -14 L 12 -14 L 12 -10 L 8 -6 L 8 12 Z" />
      <path d="M -4 -8 L 4 -2 L -2 2 L 6 8" />
    </g>
  ),
  "The Star": () => (
    <g>
      <path d="M 0 -14 L 3 -3 L 14 0 L 3 3 L 0 14 L -3 3 L -14 0 L -3 -3 Z" />
      <circle cx="0" cy="0" r="2" />
    </g>
  ),
  "The Moon": () => (
    <g>
      <path d="M 6 -12 A 14 14 0 1 0 6 12 A 10 10 0 1 1 6 -12 Z" />
    </g>
  ),
  "The Sun": () => (
    <g>
      <circle cx="0" cy="0" r="7" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
        const rad = (deg * Math.PI) / 180;
        const x1 = Math.cos(rad) * 10;
        const y1 = Math.sin(rad) * 10;
        const x2 = Math.cos(rad) * 15;
        const y2 = Math.sin(rad) * 15;
        return (
          <path key={deg} d={`M ${x1} ${y1} L ${x2} ${y2}`} />
        );
      })}
    </g>
  ),
  Judgement: () => (
    <g>
      <path d="M -12 -2 L 8 -12 L 12 -8 L 12 8 L 8 12 L -12 2 Z" />
      <path d="M -12 -2 L -12 2" />
      <circle cx="6" cy="0" r="1" />
    </g>
  ),
  "The World": () => (
    <g>
      <circle cx="0" cy="0" r="12" />
      <path d="M -14 -8 Q -18 0 -14 8" />
      <path d="M 14 -8 Q 18 0 14 8" />
      <path d="M -6 -10 Q 0 -14 6 -10" />
      <path d="M -6 10 Q 0 14 6 10" />
    </g>
  ),
};

// ─── Court glyphs (rank marks) ──────────────────────────────────────────
const COURT_RANKS = {
  Page: () => (
    <g>
      <circle cx="0" cy="0" r="2.5" />
    </g>
  ),
  Knight: () => (
    <g>
      <path d="M -5 3 L 0 -5 L 5 3" />
    </g>
  ),
  Queen: () => (
    <g>
      <path d="M -6 3 L -4 -4 L -1 1 L 0 -5 L 1 1 L 4 -4 L 6 3 Z" />
    </g>
  ),
  King: () => (
    <g>
      <path d="M -7 4 L -5 -5 L -1 2 L 0 -6 L 1 2 L 5 -5 L 7 4 Z" />
      <path d="M -7 4 L 7 4" />
    </g>
  ),
};

// ─── Card resolver ──────────────────────────────────────────────────────
export function resolveCard(title) {
  if (MajorGlyphs[title]) {
    return { type: "major", name: title };
  }
  // Minor: "<rank> of <suit>"
  const parts = title.split(" of ");
  if (parts.length !== 2) return null;
  const [rank, suit] = parts;
  if (!SUIT[suit]) return null;
  return { type: "minor", rank, suit };
}

// ─── Renderers ──────────────────────────────────────────────────────────
export function renderMajor(name) {
  const G = MajorGlyphs[name];
  if (!G) return null;
  return <G />;
}

export function renderMinor(rank, suit) {
  const Suit = SUIT[suit];
  if (!Suit) return null;

  // Court cards: one suit glyph large + rank flourish
  if (COURT_RANKS[rank]) {
    const Rank = COURT_RANKS[rank];
    return (
      <g>
        <g transform="translate(50, 50) scale(2)">
          <Suit />
        </g>
        <g transform="translate(50, 22)">
          <Rank />
        </g>
      </g>
    );
  }

  // Pip cards
  const rankToCount = {
    Ace: 1, Two: 2, Three: 3, Four: 4, Five: 5,
    Six: 6, Seven: 7, Eight: 8, Nine: 9, Ten: 10,
  };
  const count = rankToCount[rank];
  const layout = PIP_LAYOUTS[count];
  if (!layout) return null;

  return (
    <g>
      {layout.map((p, i) => (
        <g
          key={i}
          transform={`translate(${p.x}, ${p.y}) scale(${p.scale || 1})`}
        >
          <Suit />
        </g>
      ))}
    </g>
  );
}
